import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useAdminPreview } from '../../contexts/AdminPreviewContext';
import { useNotification } from '../../contexts/NotificationContext';
import AudioUploader from './AudioUploader';
import EvaluationList from './EvaluationList';
import Analytics from './Analytics';
import { useLocation } from 'react-router-dom';
import { collection, query, onSnapshot } from 'firebase/firestore';
import { db } from '../../firebaseInit';

const DEBUG_EVAL = process.env.NODE_ENV === 'development';
const evalDebug = (...args) => { if (DEBUG_EVAL) console.log(...args); };

export default function CallEvaluator() {
  evalDebug('🚀 CallEvaluator component mounted!');
  
  const location = useLocation();
  // userTeam now auto-switches when admin views another team
  const { userEmail, userId, userTeam } = useAuth();
  const { effectiveUserRole: userRole } = useAdminPreview();
  const { showSuccess, showError, showWarning, showInfo } = useNotification();
  const isBasicUser = userRole === 'basic';
  const [activeTab, setActiveTab] = useState(() => {
    const t = new URLSearchParams(location.search).get('tab');
    return ['upload', 'list', 'analytics'].includes(t) ? t : 'upload';
  });

  useEffect(() => {
    const t = new URLSearchParams(location.search).get('tab');
    if (t && ['upload', 'list', 'analytics'].includes(t)) {
      setActiveTab(t);
    }
  }, [location.search]);
  const [evaluations, setEvaluations] = useState([]);
  const [loading, setLoading] = useState(true);
  // selectedTeam is now just userTeam (which auto-switches for admins)
  const selectedTeam = userTeam;
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [selectedEvaluationId, setSelectedEvaluationId] = useState(null);

  // Handler to view a specific evaluation report from Analytics
  const handleViewReport = (evaluation) => {
    setSelectedEvaluationId(evaluation.id);
    setActiveTab('list');
  };

  evalDebug('🔑 CallEvaluator state:', { userRole, userId, userTeam, loading });

  // Access control: Admin, Team Lead, and Agent - all can access Call Evaluator
  const hasAccess = ['admin', 'team_lead', 'basic'].includes(userRole);
  const isAdmin = userRole === 'admin';
  evalDebug('🔐 Access check:', { hasAccess, userRole, isAdmin, isBasicUser, hasUserId: !!userId });
  
  useEffect(() => {
    // Check for either userEmail OR userId (userId loads faster)
    if (!hasAccess || (!userEmail && !userId)) {
      evalDebug('⚠️ Access denied or no user:', { hasAccess, hasEmail: !!userEmail, hasUserId: !!userId });
      setLoading(false);
      return;
    }

    evalDebug('Setting up evaluations listener...', { userRole, userTeam });

    // userTeam auto-switches when admin views another team
    const teamToFilter = userTeam;

    if (!teamToFilter) {
      console.warn('⚠️ No team to filter by. Team leads must have a teamId in custom claims.');
      console.warn('Try refreshing custom claims or contact admin.');
      setLoading(false);
      return;
    }
    
    // Real-time listener for evaluations - using team container path
    const collectionPath = `artifacts/my-real-estate-assistant/teams/${teamToFilter}/call-evaluations`;
    const collectionRef = collection(db, collectionPath);
    const q = query(collectionRef); // No need for where clause - path already filters by team
    
    evalDebug('Querying evaluations from team container:', collectionPath);
    
    const unsubscribe = onSnapshot(q, (snapshot) => {
      evalDebug('✅ Snapshot received, docs:', snapshot.docs.length);
      
      if (snapshot.docs.length === 0) {
        console.warn('⚠️ No documents found in collection!');
      }
      
      let evals = snapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data
        };
      });
      
      // Filter for basic users - they should only see their own evaluations
      if (isBasicUser && userEmail) {
        const beforeCount = evals.length;
        evals = evals.filter(evaluation =>
          evaluation.salesperson?.toLowerCase() === userEmail.toLowerCase()
        );
        evalDebug(`✅ Filtered from ${beforeCount} to ${evals.length} evaluations for agent`);
      }
      
      // Sort in memory instead of using orderBy
      evals.sort((a, b) => {
        const aTime = a.uploadedAt?.toMillis?.() || 0;
        const bTime = b.uploadedAt?.toMillis?.() || 0;
        return bTime - aTime;
      });
      
      evalDebug('Parsed evaluations:', evals.length);
      setEvaluations(evals);
      setLoading(false);
    }, (error) => {
      console.error('❌ Error fetching evaluations:', error);
      console.error('Error code:', error.code);
      console.error('Error message:', error.message);
      showError('Couldn\'t load evaluations. Please check your connection and try refreshing.');
      setLoading(false);
    });
    
    return () => unsubscribe();
  }, [userEmail, hasAccess, userRole, userId, isAdmin, selectedTeam, userTeam, isBasicUser, refreshTrigger]);

  // Manual refresh handler
  const handleRefresh = () => {
    console.log('🔄 Manual refresh triggered');
    setRefreshTrigger(prev => prev + 1);
  };
  
  if (!hasAccess) {
    return (
      <div className="max-w-4xl mx-auto p-4">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <h2 className="text-xl font-semibold text-red-800 mb-2">Access Denied</h2>
          <p className="text-red-600">
            You don't have permission to access this page.
          </p>
        </div>
      </div>
    );
  }
  
  return (
    <div className="flex flex-col flex-1 overflow-hidden">
      {/* Tab Navigation - white background strip */}
      <div className="flex space-x-1 border-b border-gray-200 bg-white px-4">
        <button
          onClick={() => setActiveTab('upload')}
          className={`px-4 py-2 text-sm font-medium rounded-t-lg transition-colors ${
            activeTab === 'upload'
              ? 'bg-indigo-100 text-indigo-700 border-b-2 border-indigo-600'
              : 'text-gray-600 hover:text-indigo-600 hover:bg-indigo-50'
          }`}
        >
          Upload Call
        </button>
        <button
          onClick={() => setActiveTab('list')}
          className={`px-4 py-2 text-sm font-medium rounded-t-lg transition-colors ${
            activeTab === 'list'
              ? 'bg-indigo-100 text-indigo-700 border-b-2 border-indigo-600'
              : 'text-gray-600 hover:text-indigo-600 hover:bg-indigo-50'
          }`}
        >
          {isBasicUser ? 'My Evaluations' : 'Evaluations'} ({evaluations.length})
        </button>
        <button
          onClick={() => setActiveTab('analytics')}
          className={`px-4 py-2 text-sm font-medium rounded-t-lg transition-colors ${
            activeTab === 'analytics'
              ? 'bg-indigo-100 text-indigo-700 border-b-2 border-indigo-600'
              : 'text-gray-600 hover:text-indigo-600 hover:bg-indigo-50'
          }`}
        >
          {isBasicUser ? 'My Performance' : 'Team Performance'}
        </button>
      </div>
      
      {/* Tab Content - scrollable grey area */}
      <div className="flex-1 p-4 overflow-auto bg-gray-50">
        {activeTab === 'upload' && <AudioUploader />}
        {activeTab === 'list' && (
          <EvaluationList 
            evaluations={evaluations} 
            loading={loading} 
            onRefresh={handleRefresh}
            initialSelectedId={selectedEvaluationId}
            onClearSelection={() => setSelectedEvaluationId(null)}
          />
        )}
        {activeTab === 'analytics' && <Analytics evaluations={evaluations} onViewReport={handleViewReport} />}
      </div>
    </div>
  );
}

